import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FiMail, FiShare2, FiGlobe, FiRefreshCw } from 'react-icons/fi';
import MegaphoneIcon from '../../MegaphoneIcon';
import { toast } from '../../Toast';
import api from '../../../../api/api';
import Stock, { EmailMarketingSettings } from '../../../../model/Stock';
import { setStock } from '../../../../actions/CurrentStock';
import { SettingsHeader, SettingsActions } from '../ClientSettings';
import { useClient } from '../clientSettingsData';
import { AnnouncementForm, SocialAccount, initialForm, SOCIAL_ACCOUNTS } from './announcementData';
import Rules from './Rules';
import Emails from './Emails';
import SocialPosts from './SocialPosts';
import Domain from './Domain';

export interface SectionProps {
    form: AnnouncementForm;
    setForm: (update: (f: AnnouncementForm) => AnnouncementForm) => void;
}

// left-hand nav; the url segment after /client-settings/announcements picks the section
const sections = [
    { path: '', label: 'Rules', hint: 'When an announcement arrives', icon: <MegaphoneIcon />, sub: (short: string) => `When announcements are emailed, what to skip, and who approves. Changes apply to everyone who works on ${short}.` },
    { path: 'emails', label: 'Emails', hint: 'The two automatic emails', icon: <FiMail />, sub: () => 'The two emails Investi sends on its own — both built in the email builder, no HTML.' },
    { path: 'social', label: 'Social Posts', hint: 'LinkedIn and X', icon: <FiShare2 />, sub: () => 'Post announcements to LinkedIn and X automatically when the announcement email goes out.' },
    { path: 'domain', label: 'Domain', hint: 'Send from your own domain', icon: <FiGlobe />, sub: () => 'Send from your own domain through Maileroo, and check the DNS records are in place.' },
];

// one form for all four sections, so Save / Discard cover everything and switching sections keeps unsaved edits
const AnnouncementSettings: React.FC = () => {
    const history = useHistory();
    const { section = '' } = useParams<{ section?: string }>();
    const { stock, short, ticker } = useClient();
    const current = sections.find(x => x.path === section) ?? sections[0];

    const [form, setFormState] = useState<AnnouncementForm>(() => initialForm(ticker, short));
    const [saved, setSaved] = useState<AnnouncementForm>(form);
    const [accounts, setAccounts] = useState<SocialAccount[]>(SOCIAL_ACCOUNTS);
    // DNS check in progress: shown on the Domain section and in the nav status line
    const [checking, setChecking] = useState(false);
    const dirty = JSON.stringify(form) !== JSON.stringify(saved);

    // the whole v1 email marketing settings: the v1 save overwrites every field, so Domain edits are posted on top of it
    const [emailSettings, setEmailSettings] = useState<EmailMarketingSettings>();
    const stockId = stock?.id;

    const setForm = (update: (f: AnnouncementForm) => AnnouncementForm) => setFormState(update);

    const domainOf = (settings: EmailMarketingSettings) => ({ from: settings.mailChimpReplyTo ?? '', fromName: settings.mailChimpFromName ?? '' });

    const getEmailRuleSettings = (s: Stock) => ({
        priceSensitive: { email: s.autoSendAnnouncementEmail, confirm: s.sendPriceSensitiveAnnouncements },
        other: { email: s.autoSendNonPriceSensitiveAnnouncements, confirm: s.sendNonPriceSensitiveAnnouncements },
        filter: s.autoSendFilter ?? '',
        approvers: (s.confirmationEmail ?? '').split(/[,;]/).map(x => x.trim()).filter(x => x),
    });
    const rulesJson = (f: AnnouncementForm) => JSON.stringify([f.priceSensitive, f.other, f.filter, f.approvers]);

    const dispatch = useDispatch();
    useEffect(() => {
        if (!stock) return;
        setFormState(f => ({ ...f, ...getEmailRuleSettings(stock) }));
        setSaved(f => ({ ...f, ...getEmailRuleSettings(stock) }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stockId]);

    useEffect(() => {
        if (!stockId) return;
        (async () => {
            const settings = await api().getEmailMarketingSettings(stockId);
            setEmailSettings(settings);
            setFormState(f => ({ ...f, domain: domainOf(settings) }));
            setSaved(f => ({ ...f, domain: domainOf(settings) }));
        })();
    }, [stockId]);

    const saveDomain = async () => {
        if (!stockId || !emailSettings) return false;
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const from = form.domain.from.trim();
        const fromName = form.domain.fromName.trim();
        if (from && !emailPattern.test(from)) {
            toast.error('Emails come from must be a valid email address');
            return false;
        }
        if (emailPattern.test(fromName)) {
            toast.error('From name should be your company name, not an email address');
            return false;
        }
        const result = await api().saveEmailMarketingSettings({ StockId: stockId, Settings: { ...emailSettings, mailChimpReplyTo: from, mailChimpFromName: fromName } });
        if (!result.success) {
            toast.error(result.errorMessage);
            return false;
        }
        setEmailSettings({ ...result.settings, mailChimpListId: result.settings.mailChimpListId || [] });
        return true;
    };

    const saveRules = async () => {
        if (!stock) return false;
        const request = {
            stockId: stock.id,
            autoSendAnnouncementEmail: form.priceSensitive.email,
            sendPriceSensitiveAnnouncements: form.priceSensitive.confirm,
            autoSendNonPriceSensitiveAnnouncements: form.other.email,
            sendNonPriceSensitiveAnnouncements: form.other.confirm,
            autoSendFilter: form.filter.trim(),
            confirmationEmail: form.approvers.join(', '),
        };
        const result = await api().saveAnnouncementRules(request);
        if (!result.success) {
            toast.error(result.errorMessage);
            return false;
        }
        dispatch(setStock({ ...stock, ...request, autoSendFilter: request.autoSendFilter || null }));
        return true;
    };

    // only Rules and Domain are wired; the other sections still save nothing
    const save = async () => {
        const domainDirty = JSON.stringify(form.domain) !== JSON.stringify(saved.domain);
        const rulesDirty = rulesJson(form) !== rulesJson(saved);
        if (rulesDirty) {
            const ok = await saveRules();
            if (!ok) return;
        }
        if (domainDirty) {
            const ok = await saveDomain();
            if (!ok) return;
        }
        const otherDirty = JSON.stringify({ ...form, domain: null, priceSensitive: null, other: null, filter: null, approvers: null })
            !== JSON.stringify({ ...saved, domain: null, priceSensitive: null, other: null, filter: null, approvers: null });
        setSaved(form);
        if (otherDirty) toast.info('Rules and Domain are saved; the rest of this page isn’t connected yet');
        else toast.success('Announcement settings saved');
    };

    // tablet / mobile: the nav is a sideways tab row, so bring the active item into view
    const navRef = useRef<HTMLElement>(null);
    useEffect(() => {
        const nav = navRef.current;
        const active = nav?.querySelector<HTMLElement>('button.active');
        if (!nav || !active || nav.scrollWidth <= nav.clientWidth) return;
        nav.scrollLeft = active.offsetLeft - (nav.clientWidth - active.offsetWidth) / 2;
    }, [current]);

    const actions = { dirty, onSave: save, onDiscard: () => setFormState(saved) };
    const props: SectionProps = { form, setForm };

    return <>
        <SettingsHeader title="Announcement Settings" sub={current.sub(short)} {...actions} />

        <div className="ann-row">
            <nav className="ann-nav" ref={navRef}>
                <div className="nav-title">Announcement settings</div>
                {sections.map(x => <button key={x.path} type="button" className={x === current ? 'active' : ''}
                    onClick={() => history.push(`/client-settings/announcements${x.path && `/${x.path}`}`)}>
                    {x.icon}
                    <span className="text"><strong>{x.label}</strong><small>{x.hint}</small></span>
                </button>)}
                <hr />
                {checking
                    ? <div className="status checking"><span className="badge info"><FiRefreshCw className="spin" /></span><span className="status-text">Checking your DNS records…</span></div>
                    : <div className="status"><span className="badge ok">All set</span><span className="status-text">Announcements are emailing</span></div>}
            </nav>

            <div className="cs-main">
                {current.path === '' && <Rules {...props} />}
                {current.path === 'emails' && <Emails {...props} />}
                {current.path === 'social' && <SocialPosts {...props} accounts={accounts} setAccounts={setAccounts} />}
                {current.path === 'domain' && <Domain {...props} savedFrom={saved.domain.from} checking={checking} setChecking={setChecking} />}
            </div>
        </div>

        <div className="action-bottom"><SettingsActions {...actions} /></div>
    </>;
};

export default AnnouncementSettings;
